/**
 * S7, the project overview. The most visited screen in the product.
 *
 * ONE QUESTION, answered above the fold, before any metric resolves: is my app
 * fine, where is it, and what happened last. The zone order below is the
 * spec's and it is not negotiable, because it is the order a worried person
 * asks those three things in.
 *
 *   header    the project's name, its web address, and the way to visit it
 *   zone 1    the health band. One sentence, one action, and that action is
 *             the screen's single ink block
 *   zone 3    the deploy that is actually live, with the warn strip above it
 *             when a newer one did not work
 *   zone 4    four doors, with their facts in one mono column
 *   zone 5    usage, which is allowed to arrive late
 *   zone 6    the last five things that happened
 *   advanced  the identifiers, appended below everything, never above
 *
 * THE LOAD ORDER IS A BUILD REQUIREMENT. Everything above zone 5 comes from
 * one read and paints together. Zone 5 waits an extra 900ms on purpose and
 * shows three tracks meanwhile, so the claim "the page is meaningful and
 * actionable before the metrics land" is testable rather than asserted.
 *
 * THE MOST IMPORTANT NUANCE ON THIS SCREEN is that an app can be fine while a
 * deploy is not. Studio Ledger's newest deploy did not work, so the band says
 * so and names the version that is still serving, and zone 3 shows THAT
 * version, not the one that broke, with a warn strip above it. A screen that
 * showed the failed deploy in the live slot would be telling a person their
 * site is down when it is not.
 *
 * STATES, all deep linkable, because a state a reviewer cannot open is a state
 * nobody reviews: ?state=down, ?state=approaching, ?state=over, ?state=errors,
 * ?state=deploying, ?state=metrics-loading. Paused and "the deploy did not
 * work" come from the fixture itself, on Wedding RSVP and Studio Ledger.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { ActivityList, ActivityRow } from '../../components/patterns/ActivityRow';
import { CopyField } from '../../components/patterns/CopyField';
import { DeployList, DeployRow, dateLabel } from '../../components/patterns/DeployRow';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Button } from '../../components/primitives/Button';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { SegmentedControl } from '../../components/patterns/SegmentedControl';
import { Skeleton } from '../../components/patterns/Skeleton';
import { StatTile } from '../../components/patterns/StatTile';
import { StepLedger } from '../../components/patterns/StepLedger';
import { TrafficLight, trafficVerdict } from '../../components/patterns/TrafficLight';
import type { TrafficInput } from '../../components/patterns/TrafficLight';
import { UsageStrip } from '../../components/patterns/UsageStrip';
import { VisitLink } from '../../components/patterns/VisitLink';

import {
  environmentOf,
  getProject,
  getProjectCounts,
  getProjectHealth,
  getRun,
  getUsage,
  listActivity,
  listDeploys,
  listErrors,
  listSecrets,
} from '../../lib/api';
import type { ProjectCounts, Run } from '../../lib/real-api';
import { useProjects } from '../../lib/auth/use-app-data';
import { useSessionStore } from '../../lib/auth/session';
import { provisionPlaceholderSteps, runLedgerSteps } from '../../lib/run-ledger';
import { keysAwaitingDeploy, readKeysChangedAt } from '../../lib/secrets-reminder';
import { bytes, count, relativeTime } from '../../lib/format';
import { getBundleStatus } from '../../lib/real-api';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useTicker } from '../../lib/hooks/use-ticker';
import { t } from '../../lib/i18n/t';
import { scriptFor } from '../../lib/mock/scripts';
import { fullSetupPrompt } from '../../lib/setup-prompt';
import { stateOf } from '../../lib/mock/simulate';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent, groupProject } from '../../lib/analytics/posthog';
import type { Deploy, ErrorGroup, GithubLink, Health, Project, Usage, UsageMetric } from '../../lib/types';

import '../../styles/components/overview.css';

/** The demo apparatus keeps its own clock-driven story about key changes
 *  (Keys.tsx's timeline), so this reminder is a real-build fact only. */
const USE_MOCK_OVERVIEW = import.meta.env.VITE_API_MODE === 'mock';

/** Zone 5 waits this long, once, so the load order is visible in a demo. */
export const METRICS_DELAY_MS = 900;

/** How many activity lines the overview carries before it defers to S16. */
export const ACTIVITY_ROWS = 5;

type StateName =
  | 'down'
  | 'approaching'
  | 'over'
  | 'errors'
  | 'deploying'
  | 'metrics-loading'
  | 'ready'
  | 'provisioning';

export interface TrafficInputPayload {
  project: Project;
  deploys?: Deploy[];
  usage?: Usage | null;
  /** Last hour's traffic. Absent while it loads, which reads as no traffic yet. */
  health?: Health;
}

const NO_HEALTH: Health = { downMinutes: 0, requestsLastHour: 0, errorsLastHour: 0 };

/**
 * The product's word for a resource, never the wire's name for it.
 *
 * A branch per case rather than a record read through t(variable): the
 * extractor scans for literals, so a table of bare English strings passed to
 * t() as a variable never reaches the catalogue and renders in English in
 * every language.
 */
function resourceWord(resource: UsageMetric['resource']): string {
  if (resource === 'compute') return t('compute');
  if (resource === 'file-storage') return t('file storage');
  if (resource === 'build-minutes') return t('build minutes');
  return t('database');
}

/**
 * Said when GitHub told us access to this app's repo is gone. A branch per
 * case rather than a record read through t(variable), for the same reason
 * resourceWord is: a variable never reaches the translation catalogue.
 */
function githubLinkReason(link: Exclude<GithubLink, 'ok'>, repo: string | null): string {
  const name = repo ?? t('your repository');
  if (link === 'app_removed') {
    return t('The SV Cloud GitHub App was uninstalled from {repo}.', { repo: name });
  }
  if (link === 'repo_removed') {
    return t('The SV Cloud GitHub App no longer has access to {repo}.', { repo: name });
  }
  return t('{repo} was deleted on GitHub.', { repo: name });
}

function GithubLinkNotice({
  link,
  repo,
  slug,
  onReconnect,
}: {
  link: Exclude<GithubLink, 'ok'>;
  repo: string | null;
  slug: string;
  onReconnect: () => void;
}) {
  useEffect(() => {
    captureEvent('github_link_notice_viewed', { project_slug: slug, github_link: link });
  }, [slug, link]);
  return (
    <Notice
      tone="attention"
      title={t('SV Cloud can no longer reach your GitHub repository.')}
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            captureEvent('github_link_notice_clicked', { project_slug: slug, github_link: link });
            onReconnect();
          }}
        >
          {t('Reconnect')}
        </Button>
      }
    >
      <p>{githubLinkReason(link, repo)}</p>
      <p>
        {t(
          'Your app is still running and nothing was deleted. New pushes will not deploy until you connect the repository again: install the GitHub App on it and pick the same repository. To remove the app instead, delete it from Settings.',
        )}
      </p>
    </Notice>
  );
}

function amount(metric: UsageMetric): { used: string; limit: string } {
  if (metric.unit === 'bytes') {
    return { used: bytes(metric.used), limit: bytes(metric.limit) };
  }
  return { used: count(metric.used), limit: count(metric.limit) };
}

function byNewest(deploys: Deploy[]): Deploy[] {
  return [...deploys].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/**
 * The traffic light's input, assembled from the records rather than stored.
 * The precedence itself lives in the component as a pure function, so this
 * decides nothing: it only says what is true.
 */
export function trafficInput(payload: TrafficInputPayload, forced: StateName | null): TrafficInput {
  const { project, deploys, usage } = payload;
  const health = payload.health ?? project.health ?? NO_HEALTH;
  const ordered = deploys ? byNewest(deploys) : [];
  const currentId = environmentOf(project).currentDeployId;
  const current = ordered.find((deploy) => deploy.id === currentId);

  const metrics = usage?.metrics ?? [];
  const over = metrics.find((metric) => metric.state === 'over');
  const near = metrics.find((metric) => metric.state === 'approaching');
  const compute = metrics.find((metric) => metric.resource === 'compute');

  const input: TrafficInput = {
    status: project.status,
    pausedReason: project.paused?.reason ?? null,
    downMinutes: health.downMinutes,
    // A run GitHub skipped because a newer push replaced it says nothing about
    // whether deploys work, so the verdict reads the newest one that decided.
    lastDeployFailed: ordered.find((deploy) => deploy.status !== 'skipped')?.status === 'didnt-work',
    runningSince: current ? dateLabel(current.createdAt) : '',
    // Add-ons are S15's, and no fixture add-on is broken today.
    failedAddOn: null,
    overLimitResource: over ? resourceWord(over.resource) : null,
    approaching: near
      ? {
          resource: resourceWord(near.resource),
          used: amount(near).used,
          limit: amount(near).limit,
        }
      : null,
    errorsLastHour: health.errorsLastHour,
    requestsLastHour: health.requestsLastHour,
    hasTraffic: (compute?.used ?? 0) > 0 || health.requestsLastHour > 0,
    hasDeploys: deploys ? ordered.length > 0 : undefined,
  };

  // A forced state clears everything that outranks it, because the precedence
  // table is doing its job when it hides a lower rule, and a reviewer asking
  // to see rule 6 wants rule 6.
  if (forced === 'down') {
    return { ...input, status: 'down', downMinutes: 6 };
  }

  if (forced === 'provisioning') {
    return { ...input, status: 'provisioning' };
  }

  if (forced === 'over') {
    const metric = metrics.find((m) => m.resource === 'database-storage') ?? metrics[0];
    return {
      ...input,
      lastDeployFailed: false,
      overLimitResource: metric ? resourceWord(metric.resource) : t('database'),
    };
  }

  if (forced === 'approaching') {
    const metric = metrics.find((m) => m.resource === 'database-storage') ?? metrics[0];
    return {
      ...input,
      lastDeployFailed: false,
      overLimitResource: null,
      approaching: metric
        ? {
            resource: resourceWord(metric.resource),
            used: amount(metric).used,
            limit: amount(metric).limit,
          }
        : null,
    };
  }

  if (forced === 'errors') {
    return {
      ...input,
      lastDeployFailed: false,
      overLimitResource: null,
      approaching: null,
      errorsLastHour: 14,
      requestsLastHour: 210,
    };
  }

  if (forced === 'ready') {
    return {
      ...input,
      hasDeploys: false,
      lastDeployFailed: false,
      overLimitResource: null,
      approaching: null,
      errorsLastHour: 0,
      requestsLastHour: 0,
      hasTraffic: false,
    };
  }

  return input;
}

/* -------------------------------------------------------- provisioning */

/** Same tiered backoff FirstBuild's poller uses: fast while somebody is
 *  plainly watching, slower once the run has been going a while. */
function nextPollDelay(startedAt: number): number {
  const elapsed = Date.now() - startedAt;
  if (elapsed < 15_000) return 1000;
  if (elapsed < 60_000) return 2000;
  return 5000;
}

/**
 * What the overview shows while the connect run is still building the
 * project's infrastructure.
 *
 * It is the SAME ledger step 3 of the create-project flow shows, from the
 * same `GET /runs/:id` and the same server-written step labels, a person
 * who navigated away mid-provision and came back should recognise the
 * screen, not have to work out whether it is describing the same run. The
 * zones below it are not rendered at all: there is no database, no storage,
 * no traffic and no deploy to describe yet, and painting empty ones is what
 * made the page read as finished when it was not.
 *
 * `onSettled` is what puts the real overview back, the run settling is the
 * only signal the project's status has changed, and nothing else on this
 * page is polling for it.
 */
function ProvisioningZone({
  runId,
  webAddress,
  onSettled,
}: {
  runId: string | null;
  webAddress: string;
  onSettled: () => void;
}) {
  const [run, setRun] = useState<Run | null>(null);
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  useEffect(() => {
    if (!runId) return;
    let stale = false;
    let timer: number | undefined;
    const startedAt = Date.now();

    captureEvent('project_provisioning_viewed', { run_id: runId });

    async function poll() {
      if (!runId) return;
      try {
        const next = await getRun(runId);
        if (stale) return;
        setRun(next);
        if (next.status === 'queued' || next.status === 'running') {
          timer = window.setTimeout(poll, nextPollDelay(startedAt));
        } else {
          captureEvent('project_provisioning_settled', {
            run_id: runId,
            status: next.status,
            watched_ms: Date.now() - startedAt,
            error_code: next.error?.code,
          });
          // Only a SUCCESS re-reads the project. A failed run is the one
          // thing this panel knows that the project row does not: the
          // workflow rolls its own reservation back, so a refetch would
          // either return a project with no `active_run` (and the page would
          // silently redraw as an ordinary, empty overview) or no project at
          // all. Staying put and printing the error is the honest answer.
          if (next.status === 'succeeded') settledRef.current();
        }
      } catch {
        // A failed poll is this page's problem, not the run's, the run
        // keeps going regardless, so back off and ask again rather than
        // telling somebody their setup broke.
        if (!stale) timer = window.setTimeout(poll, 5000);
      }
    }

    void poll();
    return () => {
      stale = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [runId]);

  const failed = run?.status === 'failed';

  return (
    <SectionPanel
      title={failed ? t("Setup didn't finish") : t('Setting up your app')}
      note={
        failed
          ? t('Nothing is live yet. Nobody has been charged and nothing was left half-built.')
          : t('You can leave this page. Setup keeps running.')
      }
    >
      <div className="ov__provisioning">
        <StepLedger steps={run ? runLedgerSteps(run) : provisionPlaceholderSteps()} />

        {failed ? (
          <Notice tone="attention">
            <p>{run?.error?.message ?? t('Something went wrong while setting up your project.')}</p>
          </Notice>
        ) : (
          <p className="ov__provisioning-addr">
            {t('Your web address will be {address} once this finishes.', { address: webAddress })}
          </p>
        )}
      </div>
    </SectionPanel>
  );
}

/* ---------------------------------------------------------------- screen */

export default function Overview() {
  const slug = useProjectSlug();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const forced = (params.get('state') as StateName | null) ?? null;
  const [cloneMethod, setCloneMethod] = useState<'git' | 'gh'>('git');

  // The scenario is a dependency because the demo dock can swap the whole
  // world underneath a screen, and a page holding the previous account's
  // project is the first thing that breaks a live demo.
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const timelines = useDemoStore((s) => s.timelines);
  const startTimeline = useDemoStore((s) => s.startTimeline);
  const now = useTicker();

  // Project resolution: start with cached project if available so frames paint instantly
  const projects = useProjects();
  const cachedProject = slug ? projects.find((p) => p.slug === slug) : undefined;

  const {
    data: projectFetched,
    isLoading: isProjectFetching,
    refetch: refetchProject,
  } = useAsync(async () => {
    if (!slug) return null;
    return getProject(slug);
  }, [slug, scenarioId]);

  const project = projectFetched ?? cachedProject;

  // The rail hides destinations while a project is provisioning, and it
  // reads the shell's own project copy, which is loaded once at sign-in.
  // Folding each fresh read back into it is what lets those destinations
  // come back the moment the run settles, without a page reload.
  const upsertProject = useSessionStore((s) => s.upsertProject);
  useEffect(() => {
    if (projectFetched) upsertProject(projectFetched);
  }, [projectFetched, upsertProject]);
  const isProjectLoading = isProjectFetching && !project;
  const projectId = project?.id;

  // Progressive parallel fetches: each resource arrives and updates its own zone as ready
  const { data: deploys } = useAsync(
    () => (projectId ? listDeploys(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  // The quick links' two expensive facts (how many tables, how many files).
  //
  // CACHED FOR A DAY, SERVER SIDE, and read from that cache first, always:
  // counting them means introspecting every table and walking every stored
  // object, and this screen renders two integers out of it. Nothing here ever
  // asks for a fresh count on a page view. The panel says when the numbers
  // were taken and carries the one button that measures again.
  const { data: counts } = useAsync(
    () => (projectId ? getProjectCounts(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  // What the refresh button got, which outranks the cached read for the rest
  // of the visit. Cleared when the screen moves to another project, or the
  // second project would open showing the first one's fresh numbers.
  const [recounted, setRecounted] = useState<ProjectCounts | undefined>(undefined);
  const [isRecounting, setIsRecounting] = useState(false);
  const [recountFailed, setRecountFailed] = useState(false);
  useEffect(() => {
    setRecounted(undefined);
    setRecountFailed(false);
  }, [projectId]);
  const shownCounts = recounted ?? counts;
  const { data: secrets } = useAsync(
    () => (projectId ? listSecrets(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: errors } = useAsync(
    () => (projectId ? listErrors(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: activity } = useAsync(
    () => (projectId ? listActivity(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: usage } = useAsync(
    () => (projectId ? getUsage(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  // The traffic light's last-hour numbers. Separate from the project because
  // the query behind it can take seconds, and nothing else on this page
  // should wait for it.
  const { data: health } = useAsync(
    () => (projectId ? getProjectHealth(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  // The bundle check (F27). Status only, not the whole plan: this draws one
  // sentence, and the plan costs several repository reads server-side. It
  // swallows its own failure because an overview that could not reach GitHub
  // is still an overview, and this is the least important thing on it.
  const { data: bundle } = useAsync(
    () =>
      !projectId
        ? Promise.resolve(undefined)
        : USE_MOCK_OVERVIEW
          ? // The demo apparatus has nothing to fake against, and a notice
            // nobody can see is a notice nobody reviews. One fixed status, so
            // the quiet variant renders in a design build.
            Promise.resolve({
              projectId,
              currentVersion: '2026.12.9',
              latestVersion: '2026.12.13',
              updateAvailable: true,
              legacy: false,
              baseUnavailableReason: null,
              workflowStale: false,
              workflowWarning: null,
            })
          : getBundleStatus(projectId).catch(() => undefined),
    [projectId, scenarioId],
  );

  const holdMetrics = forced === 'metrics-loading';

  // The deploying state runs on the same clock derived engine as every other
  // asynchronous thing in the product. Nothing here counts ticks.
  const timelineId = projectId ? `deploy-${projectId}` : '';
  const timeline = timelines.find((line) => line.id === timelineId);
  useEffect(() => {
    if (forced !== 'deploying' || !projectId) return;
    if (useDemoStore.getState().timelines.some((line) => line.id === timelineId)) return;
    startTimeline({
      id: timelineId,
      kind: 'redeploy',
      scriptId: 'redeploy',
      subjectId: projectId,
      startedAt: Date.now(),
      outcome: 'success',
    });
  }, [forced, projectId, timelineId, startTimeline]);

  useEffect(() => {
    if (project) {
      const input = trafficInput({ project, deploys, usage, health }, forced);
      const verdict = trafficVerdict(input);
      groupProject(project.id, {
        name: project.name,
        slug: project.slug,
        status: project.status,
      });
      captureEvent('project_overview_viewed', {
        project_id: project.id,
        project_slug: project.slug,
        verdict_reason: verdict.reason,
      });
    }
  }, [project, deploys, usage, health, forced]);

  if (isProjectLoading) {
    return (
      <div className="page">
        <Skeleton shape="panel" count={2} label={t('Loading your project')} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page">
        <Notice tone="quiet" title={t('That project is not here.')}>
          <p>{t('It may have been deleted, or the address may have a typo in it.')}</p>
        </Notice>
        <p>
          <Link className="link-inline" to="/projects">
            {t('All projects')}
          </Link>
        </p>
      </div>
    );
  }

  const base = `/p/${encodeURIComponent(project.slug)}`;
  const address = environmentOf(project).webAddress;
  const paused = project.status === 'paused';
  const provisioning = project.status === 'provisioning' || forced === 'provisioning';

  const ordered = deploys ? byNewest(deploys) : [];
  const newest = ordered[0];
  const currentId = environmentOf(project).currentDeployId;
  const current = ordered.find((deploy) => deploy.id === currentId);
  const newestDecided = ordered.find((deploy) => deploy.status !== 'skipped');
  const newerFailed =
    newestDecided !== undefined && newestDecided.status === 'didnt-work' && newestDecided !== current;

  const repoFullName = project.repo ? `${project.repo.owner}/${project.repo.name}` : project.slug;
  const setupPromptField = (
    <div className="ov__setup-cmd">
      <div className="ov__setup-head">
        <span className="ov__setup-title">{t('Paste this into your coding agent')}</span>
      </div>
      <CopyField
        label={t('Full setup prompt')}
        value={fullSetupPrompt(repoFullName)}
        onCopied={() =>
          captureEvent('overview_setup_prompt_copied', {
            project_slug: project.slug,
            has_deploys: ordered.length > 0,
          })
        }
      />
    </div>
  );

  const input = trafficInput({ project, deploys, usage, health }, forced);
  const verdict = trafficVerdict(input);

  function onBandAction() {
    captureEvent('overview_band_action_clicked', {
      project_slug: project?.slug,
      verdict_reason: verdict.reason,
    });
    switch (verdict.reason) {
      case 'down':
        navigate(`${base}/deploys`);
        return;
      case 'paused':
        navigate(project?.paused?.reason === 'quota' ? `${base}/usage` : `${base}/settings`);
        return;
      case 'deploy-failed':
        navigate(
          newestDecided ? `${base}/deploys/${newestDecided.id}/failure` : `${base}/deploys`,
        );
        return;
      case 'add-on-failed':
        navigate(`${base}/add-ons`);
        return;
      case 'over-limit':
      case 'approaching-limit':
        navigate(`${base}/usage`);
        return;
      case 'errors-elevated':
        navigate(`${base}/activity`);
        return;
      default:
        window.open(address, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Count the two facts again, now, and keep the answer for this visit.
   *
   * The ONLY caller of the refreshing read. Everything else on this screen,
   * including the first load, is served from the day-long server cache, which
   * is the whole point: a person who thinks the numbers look wrong presses
   * this, and nobody else pays for the count.
   */
  async function onRecount() {
    if (!projectId || isRecounting) return;
    setIsRecounting(true);
    setRecountFailed(false);
    captureEvent('overview_counts_refresh_clicked', {
      project_id: projectId,
      project_slug: project?.slug,
      counted_at: shownCounts?.computedAt,
      was_cached: shownCounts?.cached,
    });
    try {
      const fresh = await getProjectCounts(projectId, { refresh: true });
      setRecounted(fresh);
      captureEvent('overview_counts_refreshed', {
        project_id: projectId,
        project_slug: project?.slug,
        tables: fresh.tables,
        files: fresh.files,
        files_truncated: fresh.filesTruncated,
        changed:
          shownCounts === undefined ||
          fresh.tables !== shownCounts.tables ||
          fresh.files !== shownCounts.files,
        outcome: 'success',
      });
    } catch (error) {
      setRecountFailed(true);
      captureEvent('overview_counts_refreshed', {
        project_id: projectId,
        project_slug: project?.slug,
        outcome: 'failed',
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsRecounting(false);
    }
  }

  // The in flight row. The deploy being watched is the newest one that is not
  // the live one, so a demo of the building state on a healthy project narrates
  // the push that is actually on its way rather than inventing a commit.
  const run = timeline ? stateOf(timeline, now) : null;
  const building = run !== null && (run.status === 'running' || run.status === 'queued');
  const script = scriptFor('redeploy');
  const inFlight: Deploy | null =
    building && newest
      ? {
          ...newest,
          id: `${project.id}-in-flight`,
          status: 'deploying',
          createdAt: new Date(timeline?.startedAt ?? Date.now()).toISOString(),
          finishedAt: null,
          durationMs: null,
          isCurrent: false,
        }
      : null;

  const currentHash = current?.commitSha;
  const currentCreated = current ? new Date(current.createdAt).getTime() : 0;

  const isCurrentDeployError = (group: ErrorGroup): boolean => {
    if (!current) return true;
    if (group.deployHash && currentHash) {
      if (group.deployHash === currentHash) return true;
      if (currentHash.startsWith(group.deployHash) || group.deployHash.startsWith(currentHash)) return true;
    }
    if (currentCreated > 0 && new Date(group.lastSeen).getTime() >= currentCreated) {
      return true;
    }
    if (group.deployHash && currentHash && group.deployHash !== currentHash) {
      return false;
    }
    return true;
  };

  const currentErrors = errors ? errors.filter(isCurrentDeployError) : [];
  const openErrors = currentErrors.filter((group) => group.state !== 'resolved');
  const errorCount = errors !== undefined ? openErrors.reduce((sum, group) => sum + group.count, 0) : undefined;

  // A count that is not there yet and a count the platform could not take are
  // different facts, and neither is zero. Loading is the mono dash every tile
  // starts at; unreadable says so in words.
  const tablesFact =
    shownCounts === undefined
      ? '-'
      : shownCounts.tables === null
        ? t('Not counted')
        : shownCounts.tables === 0
          ? t('No tables yet')
          : shownCounts.tables === 1
            ? t('1 table')
            : t('{n} tables', { n: count(shownCounts.tables) });

  const filesFact =
    shownCounts === undefined
      ? '-'
      : shownCounts.files === null
        ? t('Not counted')
        : shownCounts.files === 0
          ? t('No files yet')
          : shownCounts.filesTruncated
            ? // A floor, not a total: the walk stopped at its page ceiling.
              t('{n}+ files', { n: count(shownCounts.files) })
            : shownCounts.files === 1
              ? t('1 file')
              : t('{n} files', { n: count(shownCounts.files) });

  /**
   * When the two counted facts were taken, and nothing else.
   *
   * It is on the panel permanently, not only when the answer is old: these
   * numbers are read from a cache that may be a day behind, and a number that
   * looks live and is not is the thing this line exists to prevent. The other
   * two tiles in this panel are cheap live reads and are not covered by it,
   * which the wording keeps honest by naming what was counted.
   */
  const countedNote = (() => {
    if (recountFailed) return t('Could not count again. The numbers below are the last ones taken.');
    if (isRecounting) return t('Counting the database and files again.');
    if (shownCounts === undefined) return undefined;
    const rel = relativeTime(shownCounts.computedAt);
    if (!rel) return undefined;
    return t('Database and files counted {when}.', { when: t(rel.key, { n: rel.n }) });
  })();

  const keysFact =
    secrets === undefined
      ? '-'
      : secrets.length === 1
        ? t('1 key')
        : t('{n} keys', { n: count(secrets.length) });

  const errorsFact =
    errorCount === undefined
      ? '-'
      : errorCount === 0
        ? t('No errors')
        : errorCount === 1
          ? t('1 error')
          : t('{n} errors', { n: count(errorCount) });

  const isHealthLoading =
    deploys === undefined && !forced && project.status !== 'down' && project.status !== 'paused';

  // The same "your app is running older keys than your keys page shows" fact
  // the Keys screen holds, repeated on the screen people actually open. It
  // is not a duplicate warning: a person who changed a key days ago and
  // never deployed has no reason to revisit Keys, which is exactly why the
  // reminder was being missed.
  const keysStale =
    !USE_MOCK_OVERVIEW &&
    keysAwaitingDeploy(
      readKeysChangedAt(project.id),
      deploys?.map((one) => new Date(one.createdAt).getTime()),
    );

  // Every zone below the band describes a running app. While the connect
  // run is still building one, none of them can say anything true, so the
  // page is the band plus the run's own ledger and nothing else.
  if (provisioning) {
    return (
      <div className="page">
        <header className="ovhead">
          <div className="ovhead__top">
            <h1 className="ovhead__name">{project.name}</h1>
          </div>
        </header>

        {/* The band renders no action here: the provisioning verdict's
            `action` is null, because there is nothing to act on until the
            run finishes. */}
        <TrafficLight input={{ ...input, status: 'provisioning' }} />

        <ProvisioningZone
          runId={project.activeRunId ?? null}
          webAddress={address}
          onSettled={refetchProject}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <header className="ovhead">
        <div className="ovhead__top">
          <h1 className="ovhead__name">{project.name}</h1>
          <VisitLink href={address} />
        </div>
        <div className="ovhead__addr">
          <CopyField label={t('Web address')} value={address} truncateAt={56} />
        </div>
      </header>

      {keysStale ? (
        <Notice tone="attention">
          <p>
            {t(
              'Your app is still running your old keys. It picks up the ones on your keys page on its next deploy.',
            )}
          </p>
        </Notice>
      ) : null}

      {/* GitHub access lost. Uninstalling the App, removing the repo from it,
          or deleting the repo never deletes an app (apps/cloud webhooks.ts),
          so the first thing this says is that nothing was deleted. */}
      {project.githubLink && project.githubLink !== 'ok' ? (
        <GithubLinkNotice
          link={project.githubLink}
          repo={project.repo ? `${project.repo.owner}/${project.repo.name}` : null}
          slug={project.slug}
          onReconnect={() => navigate('/new')}
        />
      ) : null}

      {/* The bundle notice. It appears exactly when there is something to do,
          which is why the screen it points at is not on the rail. Two tones,
          because they are two different facts: a failing build step is
          costing this person something now, and a newer bundle is not. */}
      {bundle?.workflowWarning ? (
        <Notice
          tone="attention"
          title={t('One of your build steps is failing.')}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                captureEvent('bundle_notice_clicked', {
                  project_slug: project.slug,
                  reason: 'workflow_stale',
                });
                navigate(`${base}/bundle`);
              }}
            >
              {t('How to fix it')}
            </Button>
          }
        >
          <p>{bundle.workflowWarning}</p>
        </Notice>
      ) : bundle?.updateAvailable ? (
        <Notice
          tone="quiet"
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                captureEvent('bundle_notice_clicked', {
                  project_slug: project.slug,
                  reason: 'update_available',
                });
                navigate(`${base}/bundle`);
              }}
            >
              {t('Have a look')}
            </Button>
          }
        >
          <p>
            {t('There is a newer bundle for your app. Nothing is wrong, and nothing changes until you ask for it.')}
          </p>
        </Notice>
      ) : null}

      {/* Zone 1. The whole screen's argument, and the one ink block. */}
      {isHealthLoading ? (
        <Skeleton shape="panel" count={1} label={t('Checking whether your app is fine')} />
      ) : (
        <TrafficLight input={input} onAction={onBandAction} />
      )}

      {/* Zones 3 to 5 dim on a paused project: they are still true, and none
          of them is something to act on until the app is switched back on. */}
      <div className="ov__rest" data-paused={paused ? 'yes' : 'no'}>
        {/* The setup prompt never goes away. Before the first deploy it leads
            the screen with the clone command and the short prompt beside it;
            after, it moves under the live deploy, the full prompt alone, so it
            can be copied again for a new computer or a new coding agent. */}
        {deploys !== undefined && ordered.length === 0 ? (
          <SectionPanel
            title={t("Let's get started")}
            note={t('Clone your repository and let your coding agent complete the setup.')}
          >
            <div className="ov__setup">
              {setupPromptField}

              <div className="ov__setup-cmd">
                <div className="ov__setup-head">
                  <span className="ov__setup-title">{t('Clone repository')}</span>
                  <SegmentedControl<'git' | 'gh'>
                    label={t('Clone command')}
                    value={cloneMethod}
                    options={[
                      { value: 'git', label: 'git' },
                      { value: 'gh', label: 'gh' },
                    ]}
                    onChange={setCloneMethod}
                  />
                </div>
                <CopyField
                  label={cloneMethod === 'git' ? t('Git clone command') : t('GitHub CLI clone command')}
                  value={
                    cloneMethod === 'git'
                      ? `git clone https://github.com/${repoFullName}.git`
                      : `gh repo clone ${repoFullName}`
                  }
                />
              </div>

              <div className="ov__setup-cmd">
                <div className="ov__setup-head">
                  <span className="ov__setup-title">{t('Prompt for your coding agent')}</span>
                </div>
                <CopyField
                  label={t('Setup prompt')}
                  value="Read SVAGENTS.md and set up the repository for SV Cloud."
                />
              </div>
            </div>
          </SectionPanel>
        ) : null}

        <SectionPanel
          title={t('Live deploy')}
          action={
            <Link className="ov__more" to={`${base}/deploys`}>
              {t('See all deploys')}
            </Link>
          }
          flush
        >
          {newerFailed ? (
            <div className="ov__strip">
              <Notice tone="attention">
                <p>{t("A newer deploy didn't work.")}</p>
              </Notice>
            </div>
          ) : null}

          {deploys === undefined ? (
            <div className="ov__strip" style={{ paddingBottom: 'var(--space-4)' }}>
              <Skeleton shape="row" count={1} label={t('Loading live deploy')} />
            </div>
          ) : current || inFlight ? (
            <DeployList label={t('What is running')}>
              {inFlight ? (
                <DeployRow
                  deploy={inFlight}
                  progress={{
                    stepLabel: t(script.steps[run?.stepIndex ?? 0].label),
                    fraction: run ? Math.min(1, run.elapsedMs / run.totalMs) : 0,
                  }}
                />
              ) : null}
              {current ? (
                <DeployRow deploy={current} to={`${base}/deploys/${current.id}`} />
              ) : null}
            </DeployList>
          ) : (
            <div className="ov__strip">
              <Notice tone="quiet">
                <p>
                  {ordered.length === 0
                    ? t("Your app isn't live yet. Nothing has deployed yet.")
                    : t("Your app isn't live yet. This was its first deploy.")}
                </p>
              </Notice>
            </div>
          )}
        </SectionPanel>

        {deploys !== undefined && ordered.length > 0 ? (
          <SectionPanel title={t('Setup prompt')}>
            <div className="ov__setup">{setupPromptField}</div>
          </SectionPanel>
        ) : null}

        {/* Zone 4. Four doors, one fact each, all four facts in one column. */}
        <SectionPanel
          title={t('Quick links')}
          note={countedNote}
          action={
            <Button
              size="sm"
              variant="ghost"
              loading={isRecounting}
              onClick={onRecount}
              aria-label={t('Count the database and files again')}
            >
              {t('Refresh')}
            </Button>
          }
          flush
        >
          <div className="ov__links">
            <StatTile
              label={t('Database')}
              fact={tablesFact}
              to={`${base}/database`}
              plain={shownCounts !== undefined && (shownCounts.tables === null || shownCounts.tables === 0)}
            />
            <StatTile
              label={t('Files')}
              fact={filesFact}
              to={`${base}/files`}
              plain={shownCounts !== undefined && (shownCounts.files === null || shownCounts.files === 0)}
            />
            <StatTile label={t('Keys and settings')} fact={keysFact} to={`${base}/keys`} />
            <StatTile
              label={t('Activity')}
              fact={errorsFact}
              to={`${base}/activity`}
              plain={errorCount !== undefined && errorCount === 0}
            />
          </div>
        </SectionPanel>

        {/* Zone 5. Allowed to arrive last. The page is complete without it. */}
        <SectionPanel
          title={t('Usage')}
          action={
            <Link className="ov__more" to={`${base}/usage`}>
              {t('See usage')}
            </Link>
          }
        >
          <UsageStrip
            usage={holdMetrics ? undefined : (usage ?? undefined)}
            waitingLabel={t('Working out your usage')}
          />
        </SectionPanel>

        <SectionPanel
          title={t('Activity')}
          action={
            <Link className="ov__more" to={`${base}/activity`}>
              {t('See all activity')}
            </Link>
          }
          flush
        >
          {activity === undefined ? (
            <div className="ov__strip" style={{ paddingBottom: 'var(--space-4)' }}>
              <Skeleton shape="row" count={5} label={t('Loading what happened')} />
            </div>
          ) : activity.length === 0 ? (
            <EmptyState
              mark="clock"
              sentence={t('Nothing has happened yet. Your first deploy will show up here.')}
            />
          ) : (
            <ActivityList label={t('What happened lately')}>
              {activity.slice(0, ACTIVITY_ROWS).map((item) => (
                <ActivityRow event={item} key={item.id} />
              ))}
            </ActivityList>
          )}
        </SectionPanel>
      </div>

      {/* Advanced appends, below everything, and never changes a zone above. */}
      <div className="ov__advanced">
        <AdvancedDisclosure>
          <dl className="idents">
            <div className="idents__row">
              <dt className="idents__key">{t('Project ID')}</dt>
              <dd className="idents__value">
                <CopyField label={t('Project ID')} value={project.id} size="inline" />
              </dd>
            </div>
            <div className="idents__row">
              <dt className="idents__key">{t('App name')}</dt>
              <dd className="idents__value">
                <CopyField label={t('App name')} value={project.slug} size="inline" />
              </dd>
            </div>
            <div className="idents__row">
              <dt className="idents__key">{t('Region')}</dt>
              <dd className="idents__value mono">{project.region}</dd>
            </div>
            <div className="idents__row">
              <dt className="idents__key">{t('Created')}</dt>
              <dd className="idents__value mono">{dateLabel(project.createdAt)}</dd>
            </div>
          </dl>
        </AdvancedDisclosure>
      </div>
    </div>
  );
}
